class MarketingPopup {
  constructor(container) {
    this.container = container;
    this.form = container.querySelector('#MarketingPopupForm');
    this.formState = container.querySelector('[data-popup-form-state]');
    this.emailInput = container.querySelector('#MarketingPopupEmail');
    this.error = container.querySelector('[data-popup-error]');
    this.result = container.querySelector('[data-popup-result]');
    this.scratchSection = container.querySelector('[data-popup-scratch]');
    this.scratchCard = container.querySelector('[data-popup-scratch-card]');
    this.canvas = container.querySelector('[data-popup-canvas]');
    this.foil = container.querySelector('[data-popup-foil]');
    this.particles = container.querySelector('[data-popup-particles]');
    this.discountTargets = container.querySelectorAll('[data-popup-discount]');
    this.codeTargets = container.querySelectorAll('[data-popup-code]');
    this.tagsInput = container.querySelector('[data-popup-tags]');
    this.closeButtons = container.querySelectorAll('[data-popup-close]');
    this.sessionDismissKey = 'stadiart-marketing-popup-session-dismissed';
    this.claimedKey = 'stadiart-marketing-popup-claimed';
    this.pendingRewardKey = 'stadiart-marketing-popup-pending-reward';
    this.claimedEmailsKey = 'stadiart-marketing-popup-claimed-emails';
    this.delay = Number(container.dataset.delaySeconds || 5) * 1000;
    this.discountOptions = (container.dataset.discountOptions || '12')
      .split(',')
      .map((value) => Number(value.trim()))
      .filter(Boolean);
    this.discountCodes = this.parseDiscountCodes(container.dataset.discountCodes || '');
    this.boundEscHandler = this.handleEsc.bind(this);
    this.isSubmitting = false;
    this.isScratching = false;
    this.scratchCompleted = false;
    this.scratchCheckScheduled = false;
    this.lastParticleAt = 0;
    this.pointerId = null;
    this.ctx = null;
    this.lastScratchPoint = null;
  }

  init() {
    if (this.shouldForceShow()) {
      this.resetStoredState();
    }

    if (!this.form) return;

    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close(true)));

    const formStatus = this.formState?.dataset.status || 'idle';
    if (formStatus === 'success') {
      if (!this.readPendingReward()) return;
      this.handleSuccessfulSubmission();
      return;
    }

    if (formStatus === 'error') {
      this.open();
      return;
    }

    if (this.wasDismissed()) return;

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));

    window.setTimeout(() => {
      if (!this.wasDismissed()) this.open();
    }, this.delay);
  }

  wasDismissed() {
    return (
      window.sessionStorage.getItem(this.sessionDismissKey) === 'true' ||
      window.localStorage.getItem(this.claimedKey) === 'true'
    );
  }

  open() {
    this.container.hidden = false;
    document.body.classList.add('marketing-popup-open');
    document.addEventListener('keydown', this.boundEscHandler);

    if (!this.form.hidden) {
      this.emailInput?.focus();
    }
  }

  close(remember = false) {
    this.container.hidden = true;
    document.body.classList.remove('marketing-popup-open');
    document.removeEventListener('keydown', this.boundEscHandler);
    if (remember) window.sessionStorage.setItem(this.sessionDismissKey, 'true');
  }

  handleEsc(event) {
    if (event.code === 'Escape') this.close(true);
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting) return;

    const email = this.emailInput?.value?.trim();
    if (!email || !this.isValidEmail(email) || !this.emailInput.checkValidity()) {
      this.error.textContent = 'Zadej prosím platný e-mail ve správném formátu.';
      this.error.hidden = false;
      return;
    }

    if (this.hasClaimedEmail(email)) {
      this.error.textContent = 'Tenhle e-mail už los v tomto zařízení získal.';
      this.error.hidden = false;
      return;
    }

    this.error.hidden = true;
    this.isSubmitting = true;
    this.form.classList.add('marketing-popup__form--submitting');
    this.form.querySelector('[type="submit"]')?.setAttribute('disabled', 'disabled');

    const discount = this.pickDiscount();
    const code = this.discountCodes[discount] || this.generateFallbackCode(discount);
    const tagValue = `newsletter,popup-sleva,discount-${discount},discount-code-${code}`;

    if (this.tagsInput) {
      this.tagsInput.value = tagValue;
    }

    window.sessionStorage.setItem(
      this.pendingRewardKey,
      JSON.stringify({ discount, code, email, tagValue })
    );

    this.form.submit();
  }

  pickDiscount() {
    if (!this.discountOptions.length) return 12;
    const index = Math.floor(Math.random() * this.discountOptions.length);
    return this.discountOptions[index];
  }

  parseDiscountCodes(rawValue) {
    return rawValue.split(',').reduce((accumulator, entry) => {
      const [discount, code] = entry.split(':');
      if (discount && code) accumulator[Number(discount.trim())] = code.trim();
      return accumulator;
    }, {});
  }

  generateFallbackCode(discount) {
    const seed = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const codeBody = Array.from({ length: 6 }, (_, index) => {
      const charIndex = (discount * 7 + index * 5) % seed.length;
      return seed[charIndex];
    }).join('');
    return `STA-${codeBody}`;
  }

  handleSuccessfulSubmission() {
    const storedReward = this.readPendingReward();
    const discount = storedReward?.discount || this.discountOptions[0] || 12;
    const code = storedReward?.code || this.discountCodes[discount] || this.generateFallbackCode(discount);
    const email = storedReward?.email || '';

    this.discountTargets.forEach((target) => {
      target.textContent = String(discount);
    });
    this.codeTargets.forEach((target) => {
      target.textContent = code;
    });

    this.form.hidden = true;
    this.scratchSection.hidden = false;
    this.result.hidden = true;
    this.container.dataset.completed = 'false';
    window.localStorage.setItem(this.claimedKey, 'true');
    if (email) this.rememberClaimedEmail(email);
    window.sessionStorage.removeItem(this.sessionDismissKey);
    window.sessionStorage.removeItem(this.pendingRewardKey);
    this.open();
    this.setupScratchCard();
  }

  setupScratchCard() {
    if (!this.canvas || !this.scratchCard || this.ctx) return;

    const rect = this.scratchCard.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(Math.round(rect.width), 1);
    const height = Math.max(Math.round(rect.height), 1);

    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.ctx.scale(dpr, dpr);
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.paintScratchSurface(width, height);

    this.canvas.addEventListener('pointerdown', (event) => this.handleScratchStart(event));
    this.canvas.addEventListener('pointermove', (event) => this.handleScratchMove(event));
    this.canvas.addEventListener('pointerup', () => this.handleScratchEnd());
    this.canvas.addEventListener('pointercancel', () => this.handleScratchEnd());
    this.canvas.addEventListener('pointerleave', () => this.handleScratchEnd());
  }

  paintScratchSurface(width, height) {
    if (!this.ctx) return;

    this.ctx.globalCompositeOperation = 'source-over';
    const gradient = this.ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#eef3f8');
    gradient.addColorStop(0.5, '#c5cfdb');
    gradient.addColorStop(1, '#9099a8');
    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(0, 0, width, height);

    for (let index = 0; index < 240; index += 1) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const alpha = 0.08 + Math.random() * 0.16;
      const size = 1 + Math.random() * 2;
      this.ctx.fillStyle = `rgba(255,255,255,${alpha})`;
      this.ctx.fillRect(x, y, size, size);
    }

    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.32)';
    this.ctx.fillRect(0, height * 0.18, width, height * 0.08);
    this.ctx.fillRect(0, height * 0.62, width, height * 0.06);
  }

  handleScratchStart(event) {
    if (this.scratchCompleted || !this.ctx) return;
    event.preventDefault();
    this.isScratching = true;
    this.pointerId = event.pointerId;
    this.lastScratchPoint = this.getScratchPoint(event);
    this.canvas.setPointerCapture?.(event.pointerId);
    this.foil?.classList.add('marketing-popup__scratch-foil--active');
    this.eraseAtPoint(this.lastScratchPoint);
  }

  handleScratchMove(event) {
    if (!this.isScratching || this.pointerId !== event.pointerId) return;
    event.preventDefault();
    this.eraseAtEvent(event);
  }

  handleScratchEnd() {
    this.isScratching = false;
    this.pointerId = null;
    this.lastScratchPoint = null;
  }

  getScratchPoint(event) {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  eraseAtPoint(point) {
    if (!point) return;

    this.ctx.save();
    this.ctx.globalCompositeOperation = 'destination-out';

    if (this.lastScratchPoint) {
      this.ctx.lineWidth = 42;
      this.ctx.beginPath();
      this.ctx.moveTo(this.lastScratchPoint.x, this.lastScratchPoint.y);
      this.ctx.lineTo(point.x, point.y);
      this.ctx.stroke();
    }

    this.ctx.beginPath();
    this.ctx.arc(point.x, point.y, 21, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.restore();

    this.emitParticles(point.x, point.y);
    this.scheduleScratchCheck();
    this.lastScratchPoint = point;
  }

  eraseAtEvent(event) {
    this.eraseAtPoint(this.getScratchPoint(event));
  }

  emitParticles(x, y) {
    if (!this.particles) return;

    const now = Date.now();
    if (now - this.lastParticleAt < 24) return;
    this.lastParticleAt = now;

    for (let index = 0; index < 4; index += 1) {
      const particle = document.createElement('span');
      const angle = Math.random() * Math.PI * 2;
      const distance = 18 + Math.random() * 28;
      const driftX = Math.cos(angle) * distance;
      const driftY = Math.sin(angle) * distance - 10;
      const size = 4 + Math.random() * 7;

      particle.className = 'marketing-popup__particle';
      particle.style.left = `${x}px`;
      particle.style.top = `${y}px`;
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.setProperty('--drift-x', `${driftX}px`);
      particle.style.setProperty('--drift-y', `${driftY}px`);
      particle.style.transform = `translate(-50%, -50%) rotate(${Math.random() * 180}deg)`;
      this.particles.appendChild(particle);
      window.setTimeout(() => particle.remove(), 700);
    }
  }

  scheduleScratchCheck() {
    if (this.scratchCheckScheduled || this.scratchCompleted) return;
    this.scratchCheckScheduled = true;

    window.setTimeout(() => {
      this.scratchCheckScheduled = false;
      this.checkScratchProgress();
    }, 120);
  }

  checkScratchProgress() {
    if (!this.ctx || this.scratchCompleted) return;

    const { width, height } = this.canvas;
    const pixels = this.ctx.getImageData(0, 0, width, height).data;
    let transparentCount = 0;

    for (let index = 3; index < pixels.length; index += 16) {
      if (pixels[index] === 0) transparentCount += 1;
    }

    const ratio = transparentCount / (pixels.length / 4 / 4);
    if (ratio >= 0.68) {
      this.completeScratch();
    }
  }

  completeScratch() {
    this.scratchCompleted = true;
    this.canvas.classList.add('marketing-popup__scratch-canvas--cleared');
    this.foil?.classList.add('marketing-popup__scratch-foil--done');
    this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
    window.setTimeout(() => {
      this.canvas.hidden = true;
      if (this.foil) this.foil.hidden = true;
    }, 360);
    this.result.hidden = false;
    this.container.dataset.completed = 'true';
  }

  getClaimedEmails() {
    try {
      const rawEmails = window.localStorage.getItem(this.claimedEmailsKey);
      const emails = rawEmails ? JSON.parse(rawEmails) : [];
      return Array.isArray(emails) ? emails : [];
    } catch (error) {
      return [];
    }
  }

  hasClaimedEmail(email) {
    const normalizedEmail = email.trim().toLowerCase();
    return this.getClaimedEmails().includes(normalizedEmail);
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email.trim());
  }

  rememberClaimedEmail(email) {
    const normalizedEmail = email.trim().toLowerCase();
    const emails = this.getClaimedEmails();
    if (emails.includes(normalizedEmail)) return;
    emails.push(normalizedEmail);
    window.localStorage.setItem(this.claimedEmailsKey, JSON.stringify(emails));
  }

  readPendingReward() {
    try {
      const rawReward = window.sessionStorage.getItem(this.pendingRewardKey);
      return rawReward ? JSON.parse(rawReward) : null;
    } catch (error) {
      return null;
    }
  }

  shouldForceShow() {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('show_popup') === '1';
  }

  resetStoredState() {
    window.sessionStorage.removeItem(this.sessionDismissKey);
    window.sessionStorage.removeItem(this.pendingRewardKey);
    window.localStorage.removeItem(this.claimedKey);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const popup = document.getElementById('MarketingPopup');
  if (!popup) return;

  new MarketingPopup(popup).init();
});
